import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Loader2, AlertCircle, User, Building2, UserPlus, Sparkles, ChevronDown, Shield, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../Logo';
import { toast } from 'sonner';

const ROLES = [
  { value: 'member', label: 'Member' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
];

export const SignupPage = ({ onSwitch }) => {
  const { signup } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', password: '', company: '', role: 'member' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [roleOpen, setRoleOpen] = useState(false);
  const lastSubmitRef = useRef(0);

  // Read pending plan from sessionStorage (set by PricingSection)
  const pendingPlan = (() => {
    try {
      const raw = sessionStorage.getItem('d3_pending_plan');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();

  // Close role dropdown when clicking outside
  const roleRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (roleRef.current && !roleRef.current.contains(e.target)) setRoleOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'At least 8 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const now = Date.now();
    if (now - lastSubmitRef.current < 2000) return;
    lastSubmitRef.current = now;

    setIsLoading(true);
    try {
      // Pass pendingPlan to backend — it sets billingRequired=true
      await signup(
        form.name, form.email, form.password, form.company, form.role,
        null, // inviteToken
        pendingPlan // backend stores billingRequired + pendingPlan
      );

      sessionStorage.removeItem('d3_pending_plan');

      if (pendingPlan && pendingPlan.planId && pendingPlan.planId !== 'enterprise') {
        // Redirect to billing setup page — it handles Stripe checkout creation
        toast.success('Account created! Setting up your trial...');
        window.history.pushState({}, '', '/billing/complete-setup');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        toast.success('Account created successfully!');
        window.history.pushState({}, '', '/dashboard');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch (err) {
      setErrors({ general: err.message });
    }
    setIsLoading(false);
  };

  const FieldError = ({ field }) => {
    if (!errors[field]) return null;
    return <p className="text-red-400 text-xs mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors[field]}</p>;
  };

  const selectedRoleLabel = ROLES.find(r => r.value === form.role)?.label || 'Member';

  return (
    <div className="min-h-screen bg-[#070b14] flex items-center justify-center px-4 dark" data-testid="signup-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="text-center mb-6 flex justify-center">
          <Logo size="large" />
        </div>

        {/* Title */}
        {pendingPlan ? (
          <div className="text-center mb-4">
            <h2 className="text-lg font-medium text-white mb-1" data-testid="signup-hero-title">
              Create your account to start your free trial
            </h2>
            <p className="text-sm text-zinc-400">
              You'll be redirected to secure checkout to activate your 14-day trial.
            </p>
          </div>
        ) : (
          <div className="text-center mb-4">
            <h2 className="text-lg font-medium text-white mb-1">Create your account</h2>
          </div>
        )}

        {/* Plan banner */}
        {pendingPlan && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20"
            data-testid="pending-plan-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-indigo-400 flex-shrink-0" />
              <p className="text-sm text-indigo-300">
                <strong className="text-indigo-200">{pendingPlan.planName}</strong> plan
                {pendingPlan.billingCycle === 'yearly' ? ' (annual billing)' : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />14-day free trial</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />No charge today</span>
              <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" />Cancel anytime</span>
            </div>
          </motion.div>
        )}

        <div className="p-8 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && (
              <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                data-testid="signup-error"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />{errors.general}
              </motion.div>
            )}

            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Full Name *</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input name="name" type="text" value={form.name} onChange={handleChange} placeholder="John Smith"
                  className={`w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border ${errors.name ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all`}
                  data-testid="signup-name-input" />
              </div>
              <FieldError field="name" />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Work Email *</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@company.com"
                  className={`w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border ${errors.email ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all`}
                  data-testid="signup-email-input" />
              </div>
              <FieldError field="email" />
            </div>

            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Password *</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} placeholder="Min. 8 characters"
                  className={`w-full h-11 pl-11 pr-11 rounded-xl bg-white/5 border ${errors.password ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all`}
                  data-testid="signup-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <FieldError field="password" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Company</label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input name="company" type="text" value={form.company} onChange={handleChange} placeholder="Acme Inc."
                    className="w-full h-11 pl-11 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all"
                    data-testid="signup-company-input" />
                </div>
              </div>
              <div className="relative" ref={roleRef}>
                <label className="text-sm text-gray-400 mb-1.5 block">Role</label>
                <button
                  type="button"
                  onClick={() => setRoleOpen(!roleOpen)}
                  className="w-full h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-blue-500/50 transition-all flex items-center justify-between"
                  data-testid="signup-role-select"
                >
                  <span className="capitalize">{selectedRoleLabel}</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${roleOpen ? 'rotate-180' : ''}`} />
                </button>
                {roleOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl bg-[#0d1221] border border-white/10 shadow-2xl overflow-hidden" data-testid="signup-role-dropdown">
                    {ROLES.map(role => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => {
                          setForm(prev => ({ ...prev, role: role.value }));
                          setRoleOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors ${
                          form.role === role.value
                            ? 'bg-blue-500/20 text-blue-400 font-medium'
                            : 'text-gray-300 hover:bg-white/5 hover:text-white'
                        }`}
                        data-testid={`signup-role-option-${role.value}`}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button type="submit" disabled={isLoading}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
              data-testid="signup-submit-btn">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <UserPlus className="w-4 h-4" />
                  {pendingPlan ? 'Create Account & Start Free Trial' : 'Create Account'}
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-gray-400 mt-6">
          Already have an account?{' '}
          <button onClick={onSwitch} className="text-blue-400 hover:text-blue-300 font-medium transition-colors" data-testid="switch-to-login">
            Sign in
          </button>
        </p>
      </motion.div>
    </div>
  );
};
